import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { FeatureLimitHint } from '../../../components/FeatureLimitHint';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import { useFeatureLimits } from '../../../hooks/useFeatureLimits';
import { useModuleAccess } from '../../../hooks/useModuleAccess';
import { SETTINGS_PATH_CHECKLISTS } from '../../../utils/settingsRoutes';
import { isSettingsPathAvailable } from '../../../lib/instanceModules';
import { AppSelectDropdown } from '../../../components/AppSelectDropdown';
import { ACTIVITY_ICON_CHOICES, ActivityIcon } from '../../../utils/activityIcons';
import {
  ACTIVITY_DOCUMENT_COMPLETION_MODE_OPTIONS,
  DEFAULT_ACTIVITY_DOCUMENT_COMPLETION_MODE,
  documentCompletionIncludesChecklist,
  type ActivityDocumentCompletionMode,
} from '../../../utils/activityDocumentCompletionMode';
import type { ActivityTypeRow, ChecklistListRow } from '../types';
import { settingsValuesEqual } from '../utils/settingsDirty';
import { SettingsCard } from '../components/SettingsCard';
import { SettingsCardSavedStatus } from '../components/SettingsCardSavedStatus';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_INPUT_CLASS,
  SETTINGS_SELECT_TRIGGER_CLASS,
} from '../utils/settingsSectionStyles';

function normalizeActivityTypeRow(raw: unknown): ActivityTypeRow | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const id = String(row.id ?? '').trim();
  if (!id) return null;
  return {
    id,
    label: String(row.label ?? id),
    icon: String(row.icon ?? 'task'),
    sort_order: typeof row.sort_order === 'number' ? row.sort_order : 0,
    document_completion_mode:
      (row.document_completion_mode as ActivityDocumentCompletionMode) ??
      DEFAULT_ACTIVITY_DOCUMENT_COMPLETION_MODE,
    default_checklist_id: row.default_checklist_id ? String(row.default_checklist_id) : null,
  };
}

export function ActivityTypesSettingsSection() {
  const [activityTypes, setActivityTypes] = React.useState<ActivityTypeRow[]>([]);
  const [baseline, setBaseline] = React.useState<ActivityTypeRow[]>([]);
  const [checklists, setChecklists] = React.useState<ChecklistListRow[]>([]);
  const [loaded, setLoaded] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const { usage, loaded: limitsReady } = useFeatureLimits();
  const activityLimit = usage('activityTypes');
  const activityTypesAtLimit = activityTypes.length >= activityLimit.max;

  const iconSelectOptions = React.useMemo(
    () =>
      ACTIVITY_ICON_CHOICES.map((opt) => ({
        value: opt.value,
        label: opt.label,
        icon: <ActivityIcon iconId={opt.value} className="h-4 w-4 text-app-text-muted" />,
      })),
    [],
  );

  const documentModeOptions = React.useMemo(
    () =>
      ACTIVITY_DOCUMENT_COMPLETION_MODE_OPTIONS.map((opt) => ({
        value: opt.value,
        label: opt.label,
      })),
    [],
  );

  // Checklisten gibt es nur mit Plantafel oder Projekte (SettingsPage rendert erst nach dem Laden der Module).
  const { modules } = useModuleAccess();
  const checklistsAvailable = isSettingsPathAvailable(SETTINGS_PATH_CHECKLISTS, modules);

  React.useEffect(() => {
    let cancelled = false;
    void Promise.all([
      fetch('/api/settings/activity-types', { credentials: 'include' }).then((res) =>
        res.ok ? res.json() : [],
      ),
      checklistsAvailable
        ? fetch('/api/settings/checklists', { credentials: 'include' }).then((res) => (res.ok ? res.json() : []))
        : Promise.resolve([]),
    ])
      .then(([typesData, checklistsData]) => {
        if (cancelled) return;
        const types = Array.isArray(typesData)
          ? typesData.map(normalizeActivityTypeRow).filter((row): row is ActivityTypeRow => Boolean(row))
          : [];
        setActivityTypes(types);
        setBaseline(types);
        setChecklists(Array.isArray(checklistsData) ? (checklistsData as ChecklistListRow[]) : []);
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [checklistsAvailable]);

  const save = async () => {
    if (activityTypes.length < 1) {
      setError('Mindestens ein Aktivitätstyp muss bestehen.');
      return;
    }
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const res = await fetch('/api/settings/activity-types', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          activityTypes.map((t, i) => ({
            key: t.id,
            label: t.label,
            icon: t.icon,
            sort_order: i,
            document_completion_mode: t.document_completion_mode,
            default_checklist_id: t.default_checklist_id,
          })),
        ),
      });
      if (res.ok) {
        const updated = await res.json();
        const types = Array.isArray(updated)
          ? updated.map(normalizeActivityTypeRow).filter((row): row is ActivityTypeRow => Boolean(row))
          : activityTypes;
        setActivityTypes(types);
        setBaseline(types);
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      } else {
        const data = (await res.json().catch(() => null)) as { error?: string } | null;
        setError(data?.error || 'Speichern fehlgeschlagen.');
      }
    } catch {
      setError('Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const add = () => {
    if (activityTypes.length >= activityLimit.max) return;
    const id = `custom_${Date.now()}`;
    setActivityTypes((prev) => [
      ...prev,
      {
        id,
        label: 'Neuer Typ',
        icon: 'task',
        sort_order: prev.length,
        document_completion_mode: DEFAULT_ACTIVITY_DOCUMENT_COMPLETION_MODE,
        default_checklist_id: null,
      },
    ]);
    setError(null);
  };

  const remove = (id: string) => {
    setActivityTypes((prev) => {
      if (prev.length <= 1) {
        setError('Mindestens ein Aktivitätstyp muss bestehen.');
        return prev;
      }
      setError(null);
      return prev.filter((t) => t.id !== id);
    });
  };

  const update = (id: string, patch: Partial<ActivityTypeRow>) => {
    setActivityTypes((prev) =>
      prev.map((t) => {
        if (t.id !== id) return t;
        const next = { ...t, ...patch };
        if (
          patch.document_completion_mode &&
          !documentCompletionIncludesChecklist(patch.document_completion_mode)
        ) {
          next.default_checklist_id = null;
        }
        return next;
      }),
    );
  };

  const canRemove = activityTypes.length > 1;

  return (
    <div className="-mx-3 -mt-3 md:mx-0 md:mt-0 space-y-3 md:space-y-5">
      <SettingsCard
        title="Aktivitäten"
        description="Diese Typen erscheinen z. B. in der Plantafel und bei Projekten. Pro Typ legen Sie Bezeichnung, Symbol und optional eine Standard-Checkliste fest und wählen, welche Abschlussdokumente die Field-App erstellt (Arbeitsbericht und/oder Checkliste). In der Plantafel gibt es dafür keine separate Einstellung."
      >
        {!loaded ? (
          <InlineLoadingState label="Lade…" className="justify-start" />
        ) : (
          <div className="space-y-4">
            <ul className="space-y-0">
              {activityTypes.map((t) => {
                const mode = t.document_completion_mode ?? DEFAULT_ACTIVITY_DOCUMENT_COMPLETION_MODE;
                const checklistEnabled = documentCompletionIncludesChecklist(mode);
                const checklistOptions = [
                  { value: '', label: 'Keine Standard-Checkliste' },
                  ...checklists
                    .filter(
                      (c) =>
                        c.id === t.default_checklist_id ||
                        !c.activity_type_key ||
                        c.activity_type_key === t.id,
                    )
                    .map((c) => ({ value: c.id, label: c.name })),
                ];
                return (
                  <li
                    key={t.id}
                    className="flex flex-wrap items-center gap-2.5 py-1.5 border-b border-app-border last:border-0"
                  >
                    <input
                      type="text"
                      value={t.label}
                      onChange={(e) => update(t.id, { label: e.target.value })}
                      className={`flex-1 min-w-[10rem] ${SETTINGS_INPUT_CLASS}`}
                      placeholder="Bezeichnung"
                    />
                    <AppSelectDropdown
                      value={mode}
                      onChange={(v) =>
                        update(t.id, {
                          document_completion_mode: v as ActivityDocumentCompletionMode,
                        })
                      }
                      options={documentModeOptions}
                      placeholder="Dokumente"
                      className="w-[min(100%,17.5rem)] shrink-0"
                      triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
                    />
                    {checklistEnabled ? (
                      <AppSelectDropdown
                        value={t.default_checklist_id ?? ''}
                        onChange={(v) => update(t.id, { default_checklist_id: v || null })}
                        options={checklistOptions}
                        placeholder="Keine Standard-Checkliste"
                        className="w-[min(100%,14rem)] shrink-0"
                        triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
                      />
                    ) : null}
                    <AppSelectDropdown
                      value={t.icon}
                      onChange={(v) => update(t.id, { icon: v })}
                      options={iconSelectOptions}
                      placeholder="Symbol wählen"
                      variant="icon-only"
                      className="w-9 shrink-0 min-w-0"
                      triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
                    />
                    <button
                      type="button"
                      onClick={() => remove(t.id)}
                      disabled={!canRemove}
                      className="p-1.5 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600 shrink-0 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-400"
                      title={canRemove ? 'Typ entfernen' : 'Mindestens ein Aktivitätstyp muss bestehen'}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                );
              })}
            </ul>
            <div className="flex flex-wrap items-center gap-2 pt-2">
              <div className="flex flex-1 items-center gap-3">
                <button
                  type="button"
                  onClick={add}
                  disabled={activityTypesAtLimit}
                  className={`${SETTINGS_BTN_OUTLINE} border-dashed border-pipedrive-blue text-pipedrive-blue hover:bg-blue-50 disabled:opacity-40`}
                >
                  <Plus className="w-3.5 h-3.5" /> Typ hinzufügen
                </button>
                <FeatureLimitHint
                  limitKey="activityTypes"
                  used={activityTypes.length}
                  max={activityLimit.max}
                  ready={limitsReady}
                />
              </div>
              <button
                type="button"
                onClick={() => void save()}
                disabled={saving || activityTypes.length === 0 || settingsValuesEqual(activityTypes, baseline)}
                className={SETTINGS_BTN_PRIMARY}
              >
                {saving ? 'Speichert…' : 'Aktivitäten speichern'}
              </button>
              <SettingsCardSavedStatus saved={saved} />
              {error ? <span className="text-sm text-red-600">{error}</span> : null}
            </div>
          </div>
        )}
      </SettingsCard>
    </div>
  );
}
